import { ROUTES } from '@/shared/constants';

import { TUser } from '@/entities/user';

import { RETURN_TO_STORAGE_KEY } from '../constants/storage';

/**
 * Код символа — пробельный (включая C0-управляющие, которые парсер URL может отбросить
 * перед разбором) или DEL. Используется на краях строки, а не через `\s`/диапазоны
 * управляющих символов в regex, чтобы не держать литеральные control-символы в исходнике.
 * @param code - код символа (`codePointAt`)
 */
const isEdgeWhitespaceOrControl = (code: number) =>
  code <= 0x20 || code === 0x7f;

/**
 * Путь безопасен для редиректа: ведёт внутрь приложения, а не на внешний домен.
 * Отсекает:
 * - абсолютные URL (`https://evil.example/...`) — не начинаются с одиночного `/`;
 * - protocol-relative (`//evil.example/...`) и его WHATWG-эквивалент `/\evil.example/...`
 *   (URL-парсер браузеров трактует `\` наравне с `/` при разборе authority для http(s),
 *   поэтому второй символ не должен быть ни `/`, ни `\`);
 * - значения с пробельным/управляющим символом на границе строки — дешёвая защита от
 *   обхода через символы, которые парсер URL может молча отбросить перед разбором.
 *
 * Значение приходит из URL ссылки, которой управляет внешняя сторона.
 * @param path - проверяемый путь
 */
const isInternalPath = (path: string) => {
  const firstCode = path.codePointAt(0) ?? 0;
  const lastCode = path.codePointAt(path.length - 1) ?? 0;

  if (
    isEdgeWhitespaceOrControl(firstCode) ||
    isEdgeWhitespaceOrControl(lastCode)
  )
    return false;

  return path === '/' || /^\/[^/\\]/.test(path);
};

/**
 * Запомнить путь возврата после входа
 * @param path - pathname + search
 */
export const saveReturnTo = (path: string) =>
  sessionStorage.setItem(RETURN_TO_STORAGE_KEY, path);

/**
 * Забрать путь возврата (одноразово). Возвращает null для отсутствующего
 * или небезопасного (ведущего на чужой домен) значения.
 */
export const consumeReturnTo = () => {
  const path = sessionStorage.getItem(RETURN_TO_STORAGE_KEY);
  sessionStorage.removeItem(RETURN_TO_STORAGE_KEY);

  return path && isInternalPath(path) ? path : null;
};

/**
 * Забыть путь возврата (logout)
 */
export const clearReturnTo = () =>
  sessionStorage.removeItem(RETURN_TO_STORAGE_KEY);

/**
 * Единственный источник истины «куда отправить пользователя после успешного входа».
 * `consumeReturnTo` вызывается безусловно — ключ гасится даже когда сохранённый путь
 * не подходит, иначе он переживёт вход другим пользователем на этом же устройстве.
 * Возврат по сохранённому пути срабатывает только для кинокомпании: ссылка на профиль
 * актёра (`/actors/:id?profile_token=…`) предназначена директору, actor туда попадать
 * не должен.
 * @param user - залогинившийся пользователь из ответа входа (может отсутствовать)
 */
export const resolvePostLoginPath = (user?: Pick<TUser, 'activity'>) => {
  const returnTo = consumeReturnTo();
  const isDirector = user?.activity?.id === 'director';

  return returnTo && isDirector ? returnTo : ROUTES.root;
};
